import { cn, Input, Slider } from "web-design-system";
import { useEffect, useState } from "react";

interface RangePickerProps {
  min: number;
  max: number;
  step: number;
  range: [number, number];
  onChange: (range: [number, number]) => void;
  className?: string;
  unit?: string;
}

export function RangePicker({
  min,
  max,
  step,
  range,
  onChange,
  className,
  unit,
}: RangePickerProps) {
  // Local state to keep slider and inputs in sync
  const [localRange, setLocalRange] = useState<[number, number]>(range);
  // Separate state for input fields to allow free typing
  const [minInput, setMinInput] = useState<string>(String(range[0]));
  const [maxInput, setMaxInput] = useState<string>(String(range[1]));

  // Update local state when external range changes
  useEffect(() => {
    setLocalRange(range);
    setMinInput(String(range[0]));
    setMaxInput(String(range[1]));
  }, [range]);

  const handleSliderChange = (val: number[]) => {
    setLocalRange(val as [number, number]);
    setMinInput(String(val[0]));
    setMaxInput(String(val[1]));
  };

  const handleSliderCommit = (val: number[]) => {
    const newRange = val as [number, number];
    onChange(newRange);
  };

  const handleMinInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMinInput(e.target.value);
  };

  const handleMaxInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMaxInput(e.target.value);
  };

  const handleMinInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (value === "" || Number.isNaN(Number(value))) {
      setMinInput(String(localRange[0]));
      return;
    }
    const numValue = Math.max(min, Math.min(Number(value), localRange[1]));
    const newRange: [number, number] = [numValue, localRange[1]];
    setLocalRange(newRange);
    setMinInput(String(numValue));
    onChange(newRange);
  };

  const handleMaxInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (value === "" || Number.isNaN(Number(value))) {
      setMaxInput(String(localRange[1]));
      return;
    }
    const numValue = Math.min(max, Math.max(Number(value), localRange[0]));
    const newRange: [number, number] = [localRange[0], numValue];
    setLocalRange(newRange);
    setMaxInput(String(numValue));
    onChange(newRange);
  };

  const handleMinInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.currentTarget.blur();
    }
  };

  const handleMaxInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.currentTarget.blur();
    }
  };

  return (
    <div className={cn("flex flex-col gap-4", className)}>
      <Slider
        className="[&_[data-slot=slider-track]]:!bg-secondary dark:[&_[data-slot=slider-track]]:!bg-secondary"
        min={min}
        max={max}
        step={step}
        value={localRange}
        onValueChange={handleSliderChange}
        onValueCommit={handleSliderCommit}
      />

      <div className="flex items-center">
        <div className="flex-1 relative">
          <Input
            className="size-full pr-5"
            type="number"
            value={minInput}
            onChange={handleMinInputChange}
            onBlur={handleMinInputBlur}
            onKeyDown={handleMinInputKeyDown}
            min={min}
            max={localRange[1]}
          />
          {unit && (
            <span className="absolute text-xs text-muted-foreground right-2 top-1/2 -translate-y-1/2">
              {unit}
            </span>
          )}
        </div>
        <span className="text-center mx-2">to</span>
        <div className="flex-1 relative">
          <Input
            className="size-full pr-5"
            type="number"
            value={maxInput}
            onChange={handleMaxInputChange}
            onBlur={handleMaxInputBlur}
            onKeyDown={handleMaxInputKeyDown}
            min={localRange[0]}
            max={max}
          />
          {unit && (
            <span className="absolute text-xs text-muted-foreground right-2 top-1/2 -translate-y-1/2">
              {unit}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
